/**
 * HWCG daily progress reports: summary text plus optional file/folder
 * attachments, each with its own comment thread. Reports are stored on the tip
 * and persisted by the owner through `onChange`.
 *
 * Locked (no add, no edit, no new comments) unless the task is
 * `Current/Ongoing`: progress is only reported while the work is underway.
 */

import React, { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ChecklistFileAttachment } from '../actionChecklistModel';
import { TicketSection } from './TicketSection';
import { ItemCommentsThread } from './ItemCommentsThread';
import ChecklistFilePicker from './ChecklistFilePicker';
import {
  DAILY_PROGRESS_ACTIVE_STATUS,
  dailyProgressLocked,
  todayCalendarDay,
  type ActionAttachment,
  type DailyProgressReport,
} from './actionTicketModel';

type DailyProgressReportsSectionProps = {
  value: DailyProgressReport[];
  /** Called with the full list after an add, edit, or comment post. */
  onChange: (next: DailyProgressReport[]) => void | Promise<void>;
  /**
   * The task's current status; anything but `Current/Ongoing` locks the
   * section.
   */
  status: string | null | undefined;
  /** Past version being viewed: nothing is writable regardless of status. */
  readOnly?: boolean;
  /** Absent for an org-scoped action, which can still pick from the org. */
  incidentId?: string | null;
  currentUserGuid: string;
  currentUserName?: string;
};

const MAX_SUMMARY = 4000;

const formatDay = (day: string): string => {
  const parsed = new Date(`${day}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? day
    : parsed.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};

const toChecklistAttachments = (
  rows: ActionAttachment[]
): ChecklistFileAttachment[] => {
  const out: ChecklistFileAttachment[] = [];
  for (const row of rows) {
    if (row.kind === 'folder' && row.folderId) {
      out.push({
        kind: 'folder',
        folderId: row.folderId,
        label: row.name,
      });
      continue;
    }
    if (row.kind === 'file' && row.fileId) {
      out.push({ kind: 'file', fileId: row.fileId, label: row.name });
    }
  }
  return out;
};

const fromChecklistAttachments = (
  rows: ChecklistFileAttachment[]
): ActionAttachment[] =>
  rows.map((row) =>
    row.kind === 'folder'
      ? {
          id: `folder-${row.folderId}`,
          name: row.label,
          kind: 'folder' as const,
          folderId: row.folderId,
        }
      : {
          id: `file-${row.fileId}`,
          name: row.label,
          kind: 'file' as const,
          fileId: row.fileId,
        }
  );

type Draft = { summary: string; attachments: ActionAttachment[] };

const emptyDraft = (): Draft => ({
  summary: '',
  attachments: [],
});

const ReportFields: React.FC<{
  draft: Draft;
  onChange: (next: Draft) => void;
  incidentId?: string | null;
  ariaPrefix: string;
  locked: boolean;
}> = ({ draft, onChange, incidentId, ariaPrefix, locked }) => (
  <div className="tw:flex tw:flex-col tw:gap-2">
    <Textarea
      value={draft.summary}
      maxLength={MAX_SUMMARY}
      rows={3}
      placeholder="What was accomplished today?"
      aria-label={`${ariaPrefix} summary`}
      disabled={locked}
      onChange={(event) => onChange({ ...draft, summary: event.target.value })}
    />
    <ChecklistFilePicker
      incidentId={incidentId}
      allowFolders
      selected={toChecklistAttachments(draft.attachments)}
      onChange={(next) =>
        onChange({ ...draft, attachments: fromChecklistAttachments(next) })
      }
    />
  </div>
);

export const DailyProgressReportsSection: React.FC<
  DailyProgressReportsSectionProps
> = ({
  value,
  onChange,
  status,
  readOnly = false,
  incidentId,
  currentUserGuid,
  currentUserName,
}) => {
  const locked = readOnly || dailyProgressLocked(status);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);
  const sorted = [...value].sort((a, b) => {
    if (a.date === b.date) return 0;
    return a.date < b.date ? 1 : -1;
  });

  const canAdd =
    draft.summary.trim() !== '' || draft.attachments.length > 0;

  const add = async () => {
    if (locked || !canAdd) return;
    const next: DailyProgressReport = {
      id: crypto.randomUUID(),
      date: todayCalendarDay(),
      summary: draft.summary.trim(),
      comments: [],
      attachments: draft.attachments,
    };
    await onChange([...value, next]);
    setDraft(emptyDraft());
    setAdding(false);
  };

  const beginEdit = (row: DailyProgressReport) => {
    setEditingId(row.id);
    setEditDraft({
      summary: row.summary,
      attachments: row.attachments ?? [],
    });
  };

  const saveEdit = async () => {
    if (locked || !editingId) return;
    if (!editDraft.summary.trim() && editDraft.attachments.length === 0) {
      return;
    }
    await onChange(
      value.map((row) => {
        if (row.id !== editingId) return row;
        const { status: _prev, ...rest } = row;
        return {
          ...rest,
          summary: editDraft.summary.trim(),
          attachments: editDraft.attachments,
        };
      })
    );
    setEditingId(null);
  };

  const setRowAttachments =
    (id: string) => (attachments: ActionAttachment[]) => {
      if (locked) return;
      void onChange(
        value.map((row) => (row.id === id ? { ...row, attachments } : row))
      );
    };

  const postComments =
    (id: string) => async (comments: DailyProgressReport['comments']) => {
      if (locked) return;
      await onChange(
        value.map((row) => (row.id === id ? { ...row, comments } : row))
      );
    };

  return (
    <TicketSection
      title="Daily Progress Reports"
      count={value.length || undefined}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={locked || adding}
          onClick={() => {
            setDraft(emptyDraft());
            setAdding(true);
          }}
        >
          <Plus />
          Add report
        </Button>
      }
    >
      <div className="tw:flex tw:flex-col tw:gap-3">
        {locked ? (
          <p role="note" className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {readOnly
              ? 'Viewing a past version; daily progress reports cannot be changed.'
              : `Daily progress reports unlock when the task status is ${DAILY_PROGRESS_ACTIVE_STATUS}.`}
          </p>
        ) : null}
        {adding && !locked ? (
          <div className="tw:flex tw:flex-col tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:p-3">
            <ReportFields
              draft={draft}
              onChange={setDraft}
              incidentId={incidentId}
              ariaPrefix="New report"
              locked={false}
            />
            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAdding(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canAdd}
                onClick={() => {
                  void add();
                }}
              >
                Save report
              </Button>
            </div>
          </div>
        ) : null}
        {sorted.length === 0 ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            No daily progress reports yet.
          </p>
        ) : (
          sorted.map((row) => {
            const editing = editingId === row.id && !locked;
            return (
              <div
                key={row.id}
                className="tw:flex tw:flex-col tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:p-3"
              >
                {editing ? (
                  <>
                    <ReportFields
                      draft={editDraft}
                      onChange={setEditDraft}
                      incidentId={incidentId}
                      ariaPrefix={`Report ${row.date}`}
                      locked={false}
                    />
                    <div className="tw:flex tw:justify-end tw:gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={
                          !editDraft.summary.trim() &&
                          editDraft.attachments.length === 0
                        }
                        onClick={() => {
                          void saveEdit();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="tw:flex tw:items-start tw:gap-2">
                    <div className="tw:min-w-0 tw:flex-1">
                      <span className="tw:text-sm tw:font-medium tw:text-foreground">
                        {formatDay(row.date)}
                      </span>
                      <p className="tw:m-0 tw:mt-1 tw:whitespace-pre-wrap tw:text-sm tw:text-foreground">
                        {row.summary || (
                          <span className="tw:text-muted-foreground">
                            No summary.
                          </span>
                        )}
                      </p>
                      {!locked ? (
                        <div className="tw:mt-2">
                          <ChecklistFilePicker
                            incidentId={incidentId}
                            allowFolders
                            selected={toChecklistAttachments(
                              row.attachments ?? []
                            )}
                            onChange={(next) =>
                              setRowAttachments(row.id)(
                                fromChecklistAttachments(next)
                              )
                            }
                          />
                        </div>
                      ) : (row.attachments?.length ?? 0) > 0 ? (
                        <ul className="tw:m-0 tw:mt-2 tw:list-disc tw:pl-5 tw:text-xs tw:text-muted-foreground">
                          {row.attachments.map((file) => (
                            <li key={file.id}>{file.name}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={locked}
                      aria-label={`Edit report ${row.date}`}
                      onClick={() => beginEdit(row)}
                    >
                      <Pencil className="tw:size-4" />
                    </Button>
                  </div>
                )}
                <ItemCommentsThread
                  value={row.comments}
                  onPost={postComments(row.id)}
                  readOnly={locked}
                  currentUserGuid={currentUserGuid}
                  currentUserName={currentUserName}
                  label={`report ${row.date}`}
                  collapsible
                  emptyText="No comments on this report yet."
                />
              </div>
            );
          })
        )}
      </div>
    </TicketSection>
  );
};

export default DailyProgressReportsSection;
